import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AlertTriangle, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const SanityFlags = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setFlags((await axios.get(`${API}/sanity/flags?limit=20`)).data.flags || []); }
    catch { setFlags([]); }
    finally { setLoading(false); }
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${API}/sanity/scan`);
      toast.success(`Scanned ${data.scanned} predictions — ${data.flagged} flagged (>${data.threshold_pp}pp disagreement)`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  useEffect(() => { load(); }, []);

  const clean = flags.length === 0;

  return (
    <section className="nd-card p-6" data-testid="sanity-flags-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          {clean ? <ShieldCheck className="w-5 h-5 text-success" /> : <AlertTriangle className="w-5 h-5 text-warning" />}
          <h2 className="font-display text-xl font-semibold">Model sanity</h2>
          <Badge
            className={`border ${
              clean
                ? "bg-success/10 text-success border-success/40"
                : "bg-warning/10 text-warning border-warning/40"
            }`}
            data-testid="sanity-status-badge"
          >
            {clean ? "clean" : `${flags.length} flag${flags.length === 1 ? "" : "s"}`}
          </Badge>
        </div>
        <Button
          onClick={scanNow}
          disabled={scanning}
          variant="outline"
          data-testid="sanity-scan-btn"
          className="h-9 rounded-full px-4 border-border bg-card hover:bg-muted text-foreground"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${scanning ? "animate-spin" : ""}`} />
          {scanning ? "Scanning" : "Scan now"}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mb-3">
        Daily at 01:00 UTC we scan stored predictions and flag any where the xG disagrees with
        the published 1X2 pick by ≥20 percentage points.
      </p>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading flags…</p>
      ) : clean ? (
        <p className="text-success text-sm" data-testid="sanity-empty">
          No disagreements. xG and 1X2 picks agree within tolerance on all recent predictions.
        </p>
      ) : (
        <ul className="divide-y divide-border" data-testid="sanity-flags-list">
          {flags.slice(0, 10).map((f) => (
            <li key={f.id} className="py-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-foreground font-medium">{f.home_team} vs {f.away_team}</span>
                <span className="text-xs text-muted-foreground">· {f.league}</span>
                <Badge className="bg-destructive/10 text-destructive border border-destructive/40 text-[10px]">
                  {f.predicted?.prediction} @{f.predicted?.confidence?.toFixed?.(1)}%
                  {" ≠ "}
                  xG→{f.expected_side}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{f.reason}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default SanityFlags;
